import React, { useCallback, useEffect, useState } from "react";

import {
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	makeStyles,
	MenuItem,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";

import ResponsiveTable from "../../components/ResponsiveTable";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { formatDuration } from "../Dashboard/format";
import { isActive, percentDone } from "./status";

const REFRESH_MS = 5000;
const FILTERS = ["all", "pending", "sent", "failed", "invalid", "skipped"];

const useStyles = makeStyles(theme => ({
	counts: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		margin: theme.spacing(1.5, 0),
	},
	message: {
		whiteSpace: "pre-wrap",
		padding: theme.spacing(1.5),
		margin: theme.spacing(1, 0, 2),
		borderRadius: theme.shape.borderRadius,
		border: theme.border ? theme.border.divider : "1px solid rgba(128,128,128,.3)",
	},
	meta: {
		color: theme.palette.text.secondary,
	},
	filter: {
		minWidth: 180,
		margin: theme.spacing(1, 0),
	},
}));

const formatDate = value =>
	value ? new Date(value).toLocaleString() : "—";

const CampaignDetails = ({ campaignId, onClose, onChanged }) => {
	const classes = useStyles();

	const [campaign, setCampaign] = useState(null);
	const [recipients, setRecipients] = useState([]);
	const [hasMore, setHasMore] = useState(false);
	const [filter, setFilter] = useState("all");
	const [page, setPage] = useState(1);

	const loadCampaign = useCallback(async () => {
		if (!campaignId) return;
		try {
			const { data } = await api.get(`/campaigns/${campaignId}`);
			setCampaign(data);
		} catch (err) {
			toastError(err);
		}
	}, [campaignId]);

	const loadRecipients = useCallback(async () => {
		if (!campaignId) return;
		try {
			const { data } = await api.get(`/campaigns/${campaignId}/recipients`, {
				params: { status: filter, pageNumber: page },
			});
			setRecipients(prev =>
				page === 1 ? data.recipients : [...prev, ...data.recipients]
			);
			setHasMore(data.hasMore);
		} catch (err) {
			toastError(err);
		}
	}, [campaignId, filter, page]);

	useEffect(() => {
		setCampaign(null);
		setRecipients([]);
		setFilter("all");
		setPage(1);
	}, [campaignId]);

	useEffect(() => {
		loadCampaign();
		if (!campaignId) return undefined;
		const timer = setInterval(loadCampaign, REFRESH_MS);
		return () => clearInterval(timer);
	}, [campaignId, loadCampaign]);

	useEffect(() => {
		loadRecipients();
	}, [loadRecipients]);

	// keep the list fresh while the campaign is sending
	useEffect(() => {
		if (!campaign || !isActive(campaign.status) || page !== 1) return undefined;
		const timer = setInterval(loadRecipients, REFRESH_MS * 2);
		return () => clearInterval(timer);
	}, [campaign, page, loadRecipients]);

	const act = async action => {
		try {
			await api.post(`/campaigns/${campaignId}/${action}`);
			await loadCampaign();
			setPage(1);
			await loadRecipients();
			onChanged();
		} catch (err) {
			toastError(err);
		}
	};

	const counts = campaign && campaign.counts;

	return (
		<Dialog open={!!campaignId} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
			<DialogTitle>{campaign ? campaign.name : ""}</DialogTitle>
			<DialogContent dividers>
				{campaign && (
					<>
						<Chip
							size="small"
							label={i18n.t(`campaigns.status.${campaign.status}`)}
							color={campaign.status === "running" ? "primary" : "default"}
						/>
						{campaign.pauseReason && campaign.status === "paused" && (
							<Typography variant="body2" color="error" style={{ marginTop: 8 }}>
								{campaign.pauseReason}
							</Typography>
						)}

						<LinearProgress
							variant="determinate"
							value={percentDone(counts)}
							style={{ margin: "16px 0 4px" }}
						/>
						<Typography variant="caption" className={classes.meta}>
							{percentDone(counts)}% ·{" "}
							{counts.pending + counts.sending > 0
								? i18n.t("campaigns.details.estimate", {
										time: formatDuration(campaign.estimatedSecondsRemaining),
								  })
								: i18n.t("campaigns.details.finished")}
						</Typography>

						<div className={classes.counts}>
							{["sent", "pending", "failed", "invalid", "skipped"].map(key => (
								<Chip
									key={key}
									size="small"
									variant="outlined"
									label={`${i18n.t(`campaigns.recipientStatus.${key}`)}: ${
										counts[key] + (key === "pending" ? counts.sending : 0)
									}`}
								/>
							))}
						</div>

						<Typography variant="subtitle2">
							{i18n.t("campaigns.details.message")}
						</Typography>
						<div className={classes.message}>{campaign.message}</div>

						<Typography variant="body2" className={classes.meta}>
							{i18n.t("campaigns.details.settings", {
								min: campaign.minDelaySeconds,
								max: campaign.maxDelaySeconds,
								hourly: campaign.hourlyLimit,
							})}
							{campaign.windowStart
								? ` · ${i18n.t("campaigns.details.window", {
										start: campaign.windowStart,
										end: campaign.windowEnd,
								  })}`
								: ""}
						</Typography>
						<Typography variant="body2" className={classes.meta}>
							{i18n.t("campaigns.details.started")}: {formatDate(campaign.startedAt)} ·{" "}
							{i18n.t("campaigns.details.finishedAt")}: {formatDate(campaign.finishedAt)}
						</Typography>

						<TextField
							select
							size="small"
							variant="outlined"
							className={classes.filter}
							label={i18n.t("campaigns.details.filter")}
							value={filter}
							onChange={e => {
								setFilter(e.target.value);
								setPage(1);
							}}
						>
							{FILTERS.map(key => (
								<MenuItem key={key} value={key}>
									{key === "all"
										? i18n.t("campaigns.details.all")
										: i18n.t(`campaigns.recipientStatus.${key}`)}
								</MenuItem>
							))}
						</TextField>

						<ResponsiveTable size="small">
							<TableHead>
								<TableRow>
									<TableCell>{i18n.t("campaigns.details.name")}</TableCell>
									<TableCell>{i18n.t("campaigns.details.number")}</TableCell>
									<TableCell>{i18n.t("campaigns.details.status")}</TableCell>
									<TableCell>{i18n.t("campaigns.details.sentAt")}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{recipients.map(r => (
									<TableRow key={r.id}>
										<TableCell>{r.name || "—"}</TableCell>
										<TableCell data-label={i18n.t("campaigns.details.number")}>
											{r.number}
										</TableCell>
										<TableCell data-label={i18n.t("campaigns.details.status")}>
											{i18n.t(`campaigns.recipientStatus.${r.status === "sending" ? "pending" : r.status}`)}
											{r.error && r.status !== "invalid" ? ` — ${r.error}` : ""}
										</TableCell>
										<TableCell data-label={i18n.t("campaigns.details.sentAt")}>
											{r.sentAt ? formatDate(r.sentAt) : "—"}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</ResponsiveTable>
						{hasMore && (
							<Button
								style={{ marginTop: 8 }}
								onClick={() => setPage(p => p + 1)}
							>
								{i18n.t("campaigns.details.more")}
							</Button>
						)}
					</>
				)}
			</DialogContent>
			<DialogActions>
				{campaign && campaign.status === "draft" && (
					<Button color="primary" variant="contained" onClick={() => act("start")}>
						{i18n.t("campaigns.buttons.start")}
					</Button>
				)}
				{campaign && campaign.status === "running" && (
					<Button variant="outlined" onClick={() => act("pause")}>
						{i18n.t("campaigns.buttons.pause")}
					</Button>
				)}
				{campaign && campaign.status === "paused" && (
					<Button color="primary" variant="contained" onClick={() => act("resume")}>
						{i18n.t("campaigns.buttons.resume")}
					</Button>
				)}
				<Button onClick={onClose}>{i18n.t("campaigns.buttons.close")}</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CampaignDetails;
